// Rolling-window partial transcript merger.
//
// Each ASR decode covers a fixed-duration window that slides forward every
// stride.  Partial text is the tail of that window, so words age left-to-right
// and drop off when their audio leaves the window.  We commit words on that
// left-edge eviction — and apply confidence gates so single-chunk
// hallucinations ("the", "learn", "just") never emit.
//
// Latency: interior words (not the rightmost / still-revising edge) can
// fast-commit once confidence is high, instead of waiting to roll off the window.

import { normalizeWord } from '../wordNormalize';

const DEFAULTS = {
  scoreThreshold:      0.50,
  fastEmitThreshold:   0.85,  // interior fast path (1 sighting)
  interiorMinScore:    0.75,  // interior fast path (2+ sightings)
  passageScoreBonus:   0.25,
  minSightings:        2,
  persistentSightings: 3,
  persistentThreshold: 0.10,
  staleLeftEdgeChunks: 1,     // index-0 held this long → window is full, commit
  staleLeftEdgeScore:  0.70,
  abandonMaxScore:     0.40,  // slot below this after 2 sightings → stop blocking
  finalizeMinScore:    0.35,  // permissive floor for last-word recovery at finalize()
};

const CONSENSUS_URL_PREFIX = 'consensus_';

/**
 * Reads consensus threshold overrides from URL search params (set by DebugMenu).
 * Params are prefixed with "consensus_" (e.g. ?consensus_fastEmitThreshold=0.75).
 * Returns an empty object when no overrides exist.
 * @returns {object}
 */
function getDebugOverrides() {
  try {
    if (typeof window === 'undefined') return {};
    const params = new URLSearchParams(window.location.search);
    const overrides = {};
    for (const key of Object.keys(DEFAULTS)) {
      const val = params.get(CONSENSUS_URL_PREFIX + key);
      if (val !== null) {
        const num = parseFloat(val);
        if (Number.isFinite(num)) {
          overrides[key] = num;
        }
      }
    }
    return overrides;
  } catch (_) {
    return {};
  }
}

function longestSuffixPrefixOverlap(prev, curr) {
  const maxLen = Math.min(prev.length, curr.length);
  for (let len = maxLen; len > 0; len--) {
    let ok = true;
    for (let i = 0; i < len; i++) {
      if (prev[prev.length - len + i] !== curr[i]) { ok = false; break; }
    }
    if (ok) return len;
  }
  return 0;
}

// Trailing partial words are often unstable (beam still revising the end of the
// window).  Leading words can be too (fresh audio entering the window).  Try
// dropping a few tokens from each side before suffix/prefix alignment so a
// hallucinated tail ("one") or revised head ("is" replacing "projects") does
// not zero out overlap and force a mass eviction of stable words.
function alignRollingPartial(prev, curr, maxDrop = 2) {
  let bestOverlap = 0;
  let bestTailDrop = 0;
  let bestHeadDrop = 0;
  for (let tailDrop = 0; tailDrop <= Math.min(maxDrop, prev.length); tailDrop++) {
    const trimmedPrev = prev.slice(0, prev.length - tailDrop);
    for (let headDrop = 0; headDrop <= Math.min(maxDrop, curr.length); headDrop++) {
      const trimmedCurr = curr.slice(headDrop);
      const len = longestSuffixPrefixOverlap(trimmedPrev, trimmedCurr);
      if (len > bestOverlap) {
        bestOverlap = len;
        bestTailDrop = tailDrop;
        bestHeadDrop = headDrop;
      }
    }
  }
  return {
    overlapLen: bestOverlap,
    evictedCount: prev.length - bestTailDrop - bestOverlap,
    headDrop: bestHeadDrop,
  };
}

function splitWords(text) {
  return text.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * Normalizes one word-list entry into the TOKENS it contributes to the wordSet,
 * using the SHARED tokenizer rather than a local copy: the wordSet has to match
 * what the decoder actually outputs, and the decoder was constrained by a grammar
 * built from this same word list through the same transform
 * (voskGrammar.buildPhraseGrammar).
 *
 * Returns an array because normalizeWord is PHRASE-granular — it maps U+2012..U+2015
 * to a space, so one entry can yield several tokens. Entries arrive whitespace-split
 * (LocalPlacementAsrHandler.wordsFromText), so a typographic em-dash compound like
 * "stopped—then" arrives whole and normalizes to "stopped then". The grammar side
 * space-joins into the acceptor, so the decoder is told to expect two symbols and
 * emits them separately; a wordSet member containing a space could never match
 * either, since lookups come from splitWords.
 *
 * A divergence here is silent and TOTAL, not a partial degradation: `_observe`
 * rewrites any word missing from the wordSet to `<unk>`, which is then filtered out
 * of emission everywhere, so the word never reaches the transcript at all. Both
 * known instances did exactly that — a copy of the tokenizer that stripped
 * apostrophes ("don't" vs "dont"), and this un-split phrase case.
 * @param {string} word
 * @returns {string[]}
 */
function normalizeForWordSet(word) {
  return normalizeWord(word).split(' ').filter(Boolean);
}

function emptySlot() {
  return {
    word: "",
    maxScore: 0,
    rawMaxScore: 0,
    sumScore: 0,
    rawSumScore: 0,
    sightings: 0,
    indexZeroChunks: 0,
    emitted: false,
  };
}

export class StreamingConsensus {
  constructor(opts = {}) {
    this._opts = { ...DEFAULTS, ...opts, ...getDebugOverrides() };
    // flatMap, not map: one entry can contribute several tokens (see
    // normalizeForWordSet). Flattening here also lets _consecutiveBigrams see the
    // split pair instead of one opaque "stopped then" element.
    const wl = (opts.wordList ?? []).flatMap((w) => normalizeForWordSet(w));
    this._wordSet = new Set(wl);
    this._consecutiveBigrams = new Set();
    for (let i = 0; i < wl.length - 1; i++) {
      if (wl[i] === wl[i + 1]) this._consecutiveBigrams.add(wl[i]);
    }
    this._prevWords = [];
    this._track = [];
    this._droppedTail = [];
    this._text = "";
  }

  get text() { return this._text; }

  processChunk(text, wordScores, chunkIndex) {
    const words = splitWords(text);
    if (words.length === 0) return { emitted: false, newWords: [] };

    const { overlapLen, evictedCount, headDrop } = alignRollingPartial(this._prevWords, words);

    const newWords = [];

    // Commit words that rolled off the left edge of the window (strict L→R).
    // Eviction is terminal — the audio has left the window and no more evidence
    // is coming — so a failed evictee is force-resolved (as finalize() does)
    // instead of blocking the evicted words behind it. Each successor still
    // faces its own eviction floor, so the confidence gate is unchanged.
    for (let i = 0; i < evictedCount; i++) {
      const w = this._tryCommitAt(this._track, i, false, true);
      if (w) newWords.push(w);
      else if (!this._track[i].emitted) this._track[i].emitted = true;
    }

    const nextTrack = [];
    for (let i = 0; i < words.length; i++) {
      const currIdx = i - headDrop;
      const prev = (currIdx >= 0 && currIdx < overlapLen && evictedCount + currIdx < this._track.length)
        ? this._track[evictedCount + currIdx]
        : emptySlot();
      // Missing score → neutral 0.5 (+ passage bonus → eff 0.75). Deliberate
      // policy: alignment is already broken, bias toward forward progress; EDM
      // reconciles errors at phrase end. Matches collectResult's text-only default.
      const slot = this._observe(prev, words[i], wordScores?.[i] ?? 0.5, i);
      nextTrack.push(slot);
      if (this._canFastCommit(slot, i, words.length, nextTrack)) {
        const w = this._tryCommitAt(nextTrack, i);
        if (w) newWords.push(w);
      }
    }

    // Tail-dropped slots (the aligner treated them as a revised beam edge) would
    // otherwise vanish with no commit attempt and no finalize() recovery. Stash
    // unemitted, unabandoned passage words instead: if the phrase progresses (any
    // later commit) they were genuine revisions and the stash is cleared; if
    // nothing follows — the window decayed at phrase end, e.g. a trailing
    // "[unk]"-only chunk — finalize() gives them the permissive last-word pass.
    if (newWords.length > 0) this._droppedTail = [];
    const tailDropCount = this._track.length - evictedCount - overlapLen;
    if (tailDropCount > 0) {
      const dropped = this._track
        .slice(this._track.length - tailDropCount)
        .filter((s) => !s.emitted && s.word && s.word !== "<unk>" && !this._isAbandoned(s));
      // Progressive decay sheds the window right-to-left across chunks (last
      // word first), so a newer drop is EARLIER speech than a surviving stash:
      // prepend to keep reading order. Clear-on-commit above bounds the stash.
      if (dropped.length > 0) this._droppedTail = [...dropped, ...this._droppedTail];
    }

    this._prevWords = words;
    this._track = nextTrack;
    return { emitted: newWords.length > 0, newWords };
  }

  finalize() {
    const newWords = [];
    for (let i = 0; i < this._track.length; i++) {
      const w = this._tryCommitAt(this._track, i, true);
      if (w) newWords.push(w);
      // Mark failed slots resolved so they don't block successors at finalize time.
      else if (!this._track[i].emitted) this._track[i].emitted = true;
    }
    // Recover tail-dropped passage words (see the stash in processChunk). Only
    // reachable when nothing committed after the drop — i.e. the window decayed
    // at phrase end — so they get the same permissive pass as track survivors.
    for (let i = 0; i < this._droppedTail.length; i++) {
      const w = this._tryCommit(this._droppedTail[i], true);
      if (w) newWords.push(w);
    }
    this._droppedTail = [];
    this._track = [];
    this._prevWords = [];
    return { text: this._text, newWords };
  }

  reset() {
    this._prevWords = [];
    this._track = [];
    this._droppedTail = [];
    this._text = "";
  }

  _effectiveRaw(slot) {
    const bonus = (this._wordSet.size > 0 && this._wordSet.has(slot.word))
      ? this._opts.passageScoreBonus : 0;
    return Math.min(1.0, slot.rawMaxScore + bonus);
  }

  _isAbandoned(slot) {
    return slot.sightings >= 2 && slot.rawMaxScore < this._opts.abandonMaxScore;
  }

  // Left-to-right within the rolling partial: do not commit slot[i] while any
  // earlier slot is still an active (non-emitted, non-abandoned) word.
  _priorSlotsResolved(slots, index) {
    for (let j = 0; j < index; j++) {
      const s = slots[j];
      if (s.emitted || this._isAbandoned(s)) continue;
      if (s.word && s.word !== "<unk>") return false;
    }
    return true;
  }

  _resolvePriorSlots(slots, index) {
    for (let j = 0; j < index; j++) {
      if (!slots[j].emitted && this._isAbandoned(slots[j])) {
        slots[j].emitted = true;
      }
    }
  }

  _tryCommitAt(slots, index, permissive = false, evicting = false) {
    const slot = slots[index];
    if (slot.emitted || !slot.word) return null;
    if (this._isAbandoned(slot)) {
      slot.emitted = true;
      return null;
    }
    if (!this._priorSlotsResolved(slots, index)) return null;
    this._resolvePriorSlots(slots, index);
    return this._tryCommit(slot, permissive, evicting);
  }

  // A prior slot blocks fast-commit only if it still looks committable.
  // Abandoned low-confidence slots (hallucinations) must not hold up successors.
  _blocksFastPath(slot) {
    if (slot.emitted || !slot.word || slot.word === "<unk>") return false;
    if (slot.sightings >= 2 && slot.rawMaxScore < this._opts.abandonMaxScore) return false;
    if (this._ready(slot)) return true;
    return slot.rawMaxScore >= 0.50 || slot.sightings < 2;
  }

  _positionStable(slot, index, len) {
    if (index < len - 1) return true;
    if (len < 3) return true;
    return this._effectiveRaw(slot) >= this._opts.fastEmitThreshold
      || (slot.sightings >= this._opts.minSightings
          && this._effectiveRaw(slot) >= this._opts.interiorMinScore);
  }

  _observe(prev, word, rawScore, index) {
    const inPassage = this._wordSet.size === 0 || this._wordSet.has(word);
    const poolWord = inPassage ? word : "<unk>";
    if (prev.emitted) {
      return {
        ...prev,
        word: poolWord,
        indexZeroChunks: index === 0 ? prev.indexZeroChunks + 1 : 0,
      };
    }

    const score = inPassage
      ? Math.min(1.0, rawScore + this._opts.passageScoreBonus)
      : rawScore;

    const slot = { ...prev, word: poolWord };
    slot.sightings++;
    slot.sumScore    += score;
    slot.rawSumScore += rawScore;
    if (score    > slot.maxScore)    slot.maxScore    = score;
    if (rawScore > slot.rawMaxScore) slot.rawMaxScore = rawScore;
    slot.indexZeroChunks = index === 0 ? prev.indexZeroChunks + 1 : 0;
    return slot;
  }

  // Emit before left-edge eviction when the word is stable enough:
  //   • interior (not the rightmost beam edge) with high confidence
  //   • index-0 held for several chunks (window full, audio moving on)
  _canFastCommit(slot, index, len, priorSlots) {
    if (slot.emitted || !slot.word) return false;
    if (!this._positionStable(slot, index, len)) return false;
    if (!this._priorSlotsResolved(priorSlots, index)) return false;
    for (let j = 0; j < index; j++) {
      if (this._blocksFastPath(priorSlots[j])) return false;
    }
    const eff = this._effectiveRaw(slot);
    if (index >= 1) {
      if (eff >= this._opts.fastEmitThreshold) return true;
      if (slot.sightings >= this._opts.minSightings
          && eff >= this._opts.interiorMinScore) return true;
    }
    if (index === 0
        && slot.indexZeroChunks >= this._opts.staleLeftEdgeChunks
        && eff >= this._opts.staleLeftEdgeScore) return true;
    return false;
  }

  _ready(slot, permissive = false, evicting = false) {
    if (!slot.word || slot.emitted) return false;
    const eff = this._effectiveRaw(slot);
    // At finalize time: passage words seen even once with a floor score survive.
    // Recovers last words that didn't accumulate a full quorum before mic cut off.
    if (permissive
        && slot.sightings >= 1
        && (this._wordSet.size === 0 || this._wordSet.has(slot.word))
        && eff >= this._opts.finalizeMinScore) return true;
    // Eviction floor: audio has left the window, no more evidence coming. Use the
    // stale-left-edge bar (0.70) for passage words — epistemically identical to the
    // stale valve but triggered by roll-off instead of index-0 tenure.
    if (evicting
        && slot.sightings >= 1
        && (this._wordSet.size === 0 || this._wordSet.has(slot.word))
        && eff >= this._opts.staleLeftEdgeScore) return true;
    return eff >= this._opts.fastEmitThreshold
      || (slot.sightings >= this._opts.minSightings && slot.maxScore >= this._opts.scoreThreshold)
      || (slot.sightings >= this._opts.persistentSightings
          && (slot.rawSumScore / slot.sightings) >= this._opts.persistentThreshold);
  }

  _tryCommit(slot, permissive = false, evicting = false) {
    if (!this._ready(slot, permissive, evicting)) {
      return null;
    }
    const w = this._append(slot.word);
    // Always mark emitted once ready: if _append rejected the word (duplicate
    // tail or <unk>), the slot must still resolve so it doesn't block
    // successors — the word is already in _text or is unprintable.
    slot.emitted = true;
    return w;
  }

  _append(word) {
    if (!word || word === "<unk>") return null;

    const tail = this._text.includes(" ")
      ? this._text.slice(this._text.lastIndexOf(" ") + 1)
      : this._text;
    // Block overlap re-emits ("the the") unless the passage has that word twice
    // in a row ("maybe maybe").
    if (tail === word && !this._consecutiveBigrams.has(word)) return null;

    this._text = this._text ? `${this._text} ${word}` : word;
    return word;
  }
}

export function createStreamingConsensus(opts) {
  return new StreamingConsensus(opts);
}

// Exported for unit tests.
export { alignRollingPartial, longestSuffixPrefixOverlap, DEFAULTS as CONSENSUS_DEFAULTS, CONSENSUS_URL_PREFIX };
